/**
 * Static connector registry — non-React module so both useConnectionStore
 * (React) and apollo.ts (module-level) can import from a single source of truth.
 */

import { initiateOAuthFlow, type OAuthInitiateResult } from "./oauthFlow";

export type ProviderId = "gitlab" | "github" | "fixtures";

export type ProviderAuthKind = "oauth" | "github-app" | "none";

export type ProviderDefinition = {
  id: ProviderId;
  displayName: string;
  authKind: ProviderAuthKind;
  hasProjectSelector: boolean;
};

export const PROVIDER_REGISTRY: Record<ProviderId, ProviderDefinition> = {
  gitlab: { id: "gitlab", displayName: "GitLab", authKind: "oauth", hasProjectSelector: true },
  github: { id: "github", displayName: "GitHub", authKind: "github-app", hasProjectSelector: true },
  fixtures: { id: "fixtures", displayName: "GitLab", authKind: "none", hasProjectSelector: true },
};

export type ProviderAuthConnection = {
  id: string;
  provider: ProviderId;
  instanceUrl?: string | null;
};

export type ProviderSignInResult = OAuthInitiateResult;

export type ProviderRefreshResult = {
  refreshed: boolean;
  needs_reauth: boolean;
  expires_in?: number;
};

export interface ProviderAuthAdapter {
  readonly provider: ProviderId;
  readonly displayName: string;
  startSignIn(): Promise<ProviderSignInResult>;
  startLink(): Promise<ProviderSignInResult>;
  startReconnect(connection: ProviderAuthConnection): Promise<ProviderSignInResult>;
  canRefreshAccessToken(): boolean;
  refreshAccessToken?(connection?: ProviderAuthConnection): Promise<ProviderRefreshResult>;
}

class GitLabAuthAdapter implements ProviderAuthAdapter {
  readonly provider = "gitlab";
  readonly displayName = PROVIDER_REGISTRY.gitlab.displayName;

  async startSignIn(): Promise<ProviderSignInResult> {
    return initiateOAuthFlow(getGitLabBaseUrl(), undefined, { usePopup: false });
  }

  async startLink(): Promise<ProviderSignInResult> {
    return initiateOAuthFlow(getGitLabBaseUrl(), undefined, {
      usePopup: false,
      intent: "link",
    });
  }

  async startReconnect(connection: ProviderAuthConnection): Promise<ProviderSignInResult> {
    return initiateOAuthFlow(resolveGitLabReconnectBaseUrl(connection), undefined, {
      usePopup: false,
    });
  }

  canRefreshAccessToken(): boolean {
    return true;
  }

  async refreshAccessToken(connection?: ProviderAuthConnection): Promise<ProviderRefreshResult> {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: connection?.id ? { "x-connection-id": connection.id } : {},
    });

    if (!response.ok) {
      return { refreshed: false, needs_reauth: true };
    }

    if (response.status === 204) {
      return { refreshed: true, needs_reauth: false };
    }

    const data = (await response.json().catch(() => null)) as { expires_in?: number } | null;
    return {
      refreshed: true,
      needs_reauth: false,
      expires_in: typeof data?.expires_in === "number" ? data.expires_in : undefined,
    };
  }
}

class GitHubAuthAdapter implements ProviderAuthAdapter {
  readonly provider = "github";
  readonly displayName = PROVIDER_REGISTRY.github.displayName;

  async startSignIn(): Promise<ProviderSignInResult> {
    return this.startAuthorize();
  }

  async startLink(): Promise<ProviderSignInResult> {
    return this.startAuthorize("link");
  }

  private async startAuthorize(intent?: "link"): Promise<ProviderSignInResult> {
    const path =
      intent === "link" ? "/api/auth/github/authorize?intent=link" : "/api/auth/github/authorize";
    const response = await fetch(path, { credentials: "include" });
    if (!response.ok) {
      throw new Error(`GitHub authorize failed: HTTP ${response.status}`);
    }

    const data = (await response.json()) as { authorize_url?: string };
    if (!data.authorize_url) {
      throw new Error("GitHub authorize response missing authorize_url.");
    }

    window.location.href = data.authorize_url;
    return { kind: "navigating" };
  }

  async startReconnect(_connection: ProviderAuthConnection): Promise<ProviderSignInResult> {
    return this.startSignIn();
  }

  canRefreshAccessToken(): boolean {
    return false;
  }
}

export const PROVIDER_AUTH_ADAPTERS = {
  gitlab: new GitLabAuthAdapter(),
  github: new GitHubAuthAdapter(),
} satisfies Partial<Record<ProviderId, ProviderAuthAdapter>>;

function getGitLabBaseUrl(): string {
  return (
    (import.meta.env.VITE_GITLAB_BASE_URL as string | undefined)?.trim() || "https://gitlab.com"
  );
}

function resolveGitLabReconnectBaseUrl(connection: ProviderAuthConnection): string {
  return connection.instanceUrl?.trim() || getGitLabBaseUrl();
}

export function getProviderDef(provider: string): ProviderDefinition | undefined {
  return PROVIDER_REGISTRY[provider as ProviderId];
}

export function getProviderAuthAdapter(provider: string): ProviderAuthAdapter | undefined {
  return PROVIDER_AUTH_ADAPTERS[provider as keyof typeof PROVIDER_AUTH_ADAPTERS];
}

/**
 * Legacy: prefer ProviderAuthAdapter capability methods for new auth control flow.
 */
export function getProviderAuthKind(provider: string): ProviderAuthKind {
  return PROVIDER_REGISTRY[provider as ProviderId]?.authKind ?? "oauth";
}
